import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InputText,
  Modal,
  Select,
  Spinner,
  Table,
  Tag,
} from '../../components'
import type { TableColumn } from '../../components'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import styles from './UsersPage.module.scss'

/**
 * Who works in the plant: their role (which sections they reach), whether they
 * are read-only, and which station groups they see. Every procedure behind this
 * screen is the `admin` section in the API: that is what protects it, and
 * `RequireSection` only keeps anybody else from staring at a screen that answers
 * FORBIDDEN to everything.
 *
 * There is no mirror data here — `app.users` is WASP's own table — so no
 * freshness badge: this screen is never stale.
 */

type PlantUser = RouterOutputs['users']['list'][number]
type PlantRole = RouterOutputs['roles']['list'][number]
/** Which station groups a person sees: all of them, or the ones they are assigned to. */
type GroupScope = PlantUser['role']

/** The API's floor, and the one this screen disables its submits on. */
const MIN_PASSWORD_LENGTH = 8

/** Four digits, as `pinSchema` in the API: the PIN of almacén's tablet. */
const PIN = /^\d{4}$/

const SCOPE_LABEL: Record<GroupScope, string> = { manager: 'Todos los grupos', operator: 'Sus grupos' }

const SCOPE_OPTIONS = [
  { value: 'operator', label: `${SCOPE_LABEL.operator} (operario)` },
  { value: 'manager', label: `${SCOPE_LABEL.manager} (gestor)` },
]

/**
 * A `<select>` hands back a plain string, and the scope is a union. Narrowed
 * with a guard rather than cast: a value that is neither is a bug in the options
 * list and falling back to the current one is the honest answer to it.
 */
const isGroupScope = (value: string): value is GroupScope => value === 'manager' || value === 'operator'

const roleOptions = (roles: Array<PlantRole>) => roles.map((role) => ({ value: role.id, label: role.name }))

const SELF_ACCESS_NOTE = 'No puedes cambiar tu propio acceso: te quedarías fuera de la gestión.'
const SELF_DEACTIVATE_NOTE = 'No puedes desactivar tu propia cuenta.'

export const UsersPage = (): ReactElement => {
  const { user: session } = useSession()
  const utils = trpc.useUtils()

  const [includeInactive, setIncludeInactive] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PlantUser | null>(null)
  const [resetting, setResetting] = useState<PlantUser | null>(null)
  const [deactivating, setDeactivating] = useState<PlantUser | null>(null)
  // The failure of an action taken FROM a row (reactivate, deactivate), shown
  // above the table: neither has a dialog left open to show it in.
  const [actionError, setActionError] = useState<string | null>(null)

  const usersQuery = trpc.users.list.useQuery({ includeInactive })
  // The roles a person can be given, each with the sections it opens — for the
  // forms, and to name each row's role in the table.
  const rolesQuery = trpc.roles.list.useQuery()
  const roles = rolesQuery.data ?? []
  const roleName = (roleId: string | null): string =>
    roles.find((role) => role.id === roleId)?.name ?? (roleId === null ? 'Sin rol' : '…')

  // No input: it invalidates the list for BOTH values of `includeInactive`, so
  // toggling after a save does not show the answer from before it.
  const refreshList = (): void => {
    void utils.users.list.invalidate()
  }

  const reactivate = trpc.users.reactivate.useMutation({
    onSuccess: () => {
      setActionError(null)
      refreshList()
    },
    onError: (error) => setActionError(trpcMessage(error)),
  })

  const deactivate = trpc.users.deactivate.useMutation({
    onSuccess: () => {
      setDeactivating(null)
      setActionError(null)
      refreshList()
    },
    onError: (error) => {
      setDeactivating(null)
      setActionError(trpcMessage(error))
    },
  })

  const isSelf = (row: PlantUser): boolean => row.id === session?.userId

  const columns: Array<TableColumn<PlantUser>> = [
    {
      key: 'name',
      header: 'Nombre',
      render: (row) => (
        <span className={styles['users-page__name']}>
          {row.name}
          {isSelf(row) && <Tag tone="info" label="Tú" />}
        </span>
      ),
    },
    { key: 'email', header: 'Email', render: (row) => row.email, hideOnMobile: true },
    {
      key: 'role',
      header: 'Rol',
      render: (row) => (
        <span className={styles['users-page__name']}>
          <Tag tone="info" label={roleName(row.roleId)} />
          {row.readOnly ? <Tag tone="neutral" label="Solo lectura" /> : null}
          {row.hasPin ? <Tag tone="neutral" label="PIN" /> : null}
        </span>
      ),
    },
    {
      key: 'scope',
      header: 'Grupos que ve',
      render: (row) => <Tag tone="neutral" label={SCOPE_LABEL[row.role]} />,
      hideOnMobile: true,
    },
    {
      key: 'state',
      header: 'Estado',
      render: (row) => (row.active
        ? <Tag tone="ok" label="Activo" />
        : <Tag tone="warn" label="Inactivo" />),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (row) => (
        <div className={styles['users-page__row-actions']}>
          {/* The visible label is one word — the accessible name carries the row,
              so a spec (and a screen reader) can tell three "Editar" apart. */}
          <Button
            variant="secondary"
            onClick={() => setEditing(row)}
            aria-label={`Editar ${row.name}`}
            label="Editar"
          />
          <Button
            variant="secondary"
            onClick={() => setResetting(row)}
            aria-label={`Cambiar la contraseña de ${row.name}`}
            label="Contraseña"
          />
          {row.active
            ? (
              <>
                {/* Disabled and not hidden: the button says the action exists,
                    and the note next to it says why it is not yours to take —
                    written out rather than left to a `title`, which a disabled
                    control never shows. The API refuses it too
                    (PRECONDITION_FAILED); this only saves the round-trip. */}
                <Button
                  variant="danger"
                  onClick={() => setDeactivating(row)}
                  disabled={isSelf(row)}
                  aria-label={`Desactivar ${row.name}`}
                  label="Desactivar"
                />
                {isSelf(row) && (
                  <span className={styles['users-page__row-note']}>{SELF_DEACTIVATE_NOTE}</span>
                )}
              </>
            )
            : (
              <Button
                onClick={() => {
                  setActionError(null)
                  reactivate.mutate({ userId: row.id })
                }}
                disabled={reactivate.isPending}
                aria-label={`Reactivar ${row.name}`}
                label="Reactivar"
              />
            )}
        </div>
      ),
    },
  ]

  return (
    <section className={styles['users-page']}>
      <header className={styles['users-page__header']}>
        <h1 className={styles['users-page__title']}>Usuarios</h1>
        <Button onClick={() => setCreating(true)} label="Nuevo usuario" />
      </header>

      <label className={styles['users-page__toggle']}>
        <input
          type="checkbox"
          className={styles['users-page__checkbox']}
          checked={includeInactive}
          onChange={(event) => setIncludeInactive(event.target.checked)}
        />
        Mostrar inactivos
      </label>

      {actionError !== null && (
        <p role="alert" className={styles['users-page__error']}>{actionError}</p>
      )}

      <UsersList
        columns={columns}
        isPending={usersQuery.isPending}
        isError={usersQuery.isError}
        error={usersQuery.error}
        rows={usersQuery.data ?? []}
        onRetry={() => { void usersQuery.refetch() }}
        onCreate={() => setCreating(true)}
      />

      {creating && (
        <CreateUserDialog
          roles={roles}
          onClose={() => setCreating(false)}
          onCreated={(warning) => {
            setCreating(false)
            setActionError(warning)
            refreshList()
          }}
        />
      )}

      {editing !== null && (
        <EditUserDialog
          user={editing}
          roles={roles}
          isSelf={isSelf(editing)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            refreshList()
          }}
        />
      )}

      {resetting !== null && (
        <ResetPasswordFlow
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}

      <ConfirmDialog
        open={deactivating !== null}
        variant="danger"
        title="Desactivar usuario"
        message={deactivating === null
          ? ''
          : `${deactivating.name} dejará de poder entrar y se cerrarán sus sesiones. Sus asignaciones se conservan.`}
        confirmLabel="Desactivar"
        isLoading={deactivate.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => {
          if (deactivating !== null) {
            deactivate.mutate({ userId: deactivating.id })
          }
        }}
      />
    </section>
  )
}

type UsersListProps = {
  columns: Array<TableColumn<PlantUser>>
  isPending: boolean
  isError: boolean
  error: unknown
  rows: Array<PlantUser>
  onRetry: () => void
  onCreate: () => void
}

/**
 * Loading, failed, empty and full — four visibly different things. An empty
 * table for a failed request tells a manager the plant has no staff.
 */
const UsersList = ({
  columns,
  isPending,
  isError,
  error,
  rows,
  onRetry,
  onCreate,
}: UsersListProps): ReactElement => {
  if (isPending) {
    return <Spinner label="Cargando usuarios…" />
  }
  if (isError) {
    return <ErrorState error={error} onRetry={onRetry} />
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No hay usuarios"
        message="Crea el primero para que pueda entrar en la aplicación de planta."
        action={<Button onClick={onCreate} label="Crear el primer usuario" />}
      />
    )
  }
  return <Table columns={columns} rows={rows} rowKey={(row) => row.id} caption="Usuarios de planta" />
}

type PinFieldProps = {
  label: string
  hint: string
  value: string
  onChange: (pin: string) => void
}

/** Four digits for almacén's tablet; anything but a digit is dropped as it is typed. */
const PinField = ({ label, hint, value, onChange }: PinFieldProps): ReactElement => (
  <InputText
    label={label}
    type="password"
    inputMode="numeric"
    autoComplete="off"
    maxLength={4}
    hint={hint}
    value={value}
    onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
  />
)

type ReadOnlyCheckboxProps = {
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}

/** The read-only flag: the person reaches their role's screens but changes nothing. */
const ReadOnlyCheckbox = ({ checked, disabled, onChange }: ReadOnlyCheckboxProps): ReactElement => (
  <label className={styles['users-page__toggle']}>
    <input
      type="checkbox"
      className={styles['users-page__checkbox']}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    Solo lectura
  </label>
)

type CreateUserDialogProps = {
  roles: Array<PlantRole>
  onClose: () => void
  /** With what to tell the manager when the person was created but their PIN was not. */
  onCreated: (warning: string | null) => void
}

/**
 * A new person. Creating the row is what lets them in — by Google too: nobody
 * is created on a first Google sign-in. The password is optional: without one
 * the person can only enter with Google. With one, it is read out to them —
 * there is no email sender in WASP, so there is no invitation link either.
 * The PIN, optional too, is set right after the person exists: it is a
 * second call, so a failure there leaves the person created and says so.
 */
const CreateUserDialog = ({ roles, onClose, onCreated }: CreateUserDialogProps): ReactElement => {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [roleId, setRoleId] = useState('')
  const [readOnly, setReadOnly] = useState(false)
  const [scope, setScope] = useState<GroupScope>('operator')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const create = trpc.users.create.useMutation()
  const setUserPin = trpc.users.setPin.useMutation()

  const passwordTooShort = password !== '' && password.length < MIN_PASSWORD_LENGTH
  const pinIncomplete = pin !== '' && !PIN.test(pin)
  const incomplete = email === '' || name === '' || roleId === '' || passwordTooShort || pinIncomplete
  const isPending = create.isPending || setUserPin.isPending

  const submit = async (): Promise<void> => {
    let createdId: string
    try {
      const created = await create.mutateAsync({
        email,
        name,
        role: scope,
        roleId,
        readOnly,
        ...(password === '' ? {} : { password }),
      })
      createdId = created.id
    } catch (error) {
      setErrorMessage(trpcMessage(error))
      return
    }
    if (pin === '') {
      onCreated(null)
      return
    }
    try {
      await setUserPin.mutateAsync({ userId: createdId, pin })
      onCreated(null)
    } catch (error) {
      onCreated(`${name} se ha creado, pero su PIN no: ${trpcMessage(error)} Ponlo desde «Editar».`)
    }
  }

  return (
    <Modal open title="Nuevo usuario" onClose={onClose} maxWidth="48rem">
      <form
        className={styles['users-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          void submit()
        }}
      >
        <InputText
          label="Email"
          type="email"
          autoComplete="off"
          inputMode="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <InputText
          label="Nombre"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <Select
          label="Rol"
          options={roleOptions(roles)}
          placeholder="Elige un rol…"
          value={roleId}
          onChange={(event) => setRoleId(event.target.value)}
          required
        />
        <ReadOnlyCheckbox checked={readOnly} onChange={setReadOnly} />
        <Select
          label="Grupos que ve"
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={(event) => {
            const { value } = event.target
            if (isGroupScope(value)) {
              setScope(value)
            }
          }}
        />
        <InputText
          label="Contraseña (opcional)"
          type="password"
          autoComplete="new-password"
          hint={`Sin contraseña, solo podrá entrar con Google. Con ella, mínimo ${MIN_PASSWORD_LENGTH} caracteres: dísela a la persona, no se envía ningún correo.`}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <PinField
          label="PIN (opcional)"
          hint="Cuatro dígitos para entrar en la tablet de almacén, si su rol abre almacén. Díselo a la persona."
          value={pin}
          onChange={setPin}
        />
        {errorMessage !== null && (
          <p role="alert" className={styles['users-page__error']}>{errorMessage}</p>
        )}
        <div className={styles['users-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={isPending} label="Cancelar" />
          <Button
            type="submit"
            disabled={isPending || incomplete}
            label={isPending ? 'Creando…' : 'Crear usuario'}
          />
        </div>
      </form>
    </Modal>
  )
}

type EditUserDialogProps = {
  user: PlantUser
  roles: Array<PlantRole>
  /** The caller's own row: the access fields are theirs to look at, not to change. */
  isSelf: boolean
  onClose: () => void
  onSaved: () => void
}

/**
 * Name, email, role, read-only and the groups one sees. `users.update` refuses
 * an input with none of them (a 200 that changes nothing yet reads as a
 * successful save), so the submit stays disabled until something actually
 * differs — and only what differs travels. A new PIN goes by `users.setPin`
 * (also for oneself: it is no access field), and «Quitar PIN» asks first, in
 * its own dialog instead of this one, as the password reset does.
 */
const EditUserDialog = ({ user, roles, isSelf, onClose, onSaved }: EditUserDialogProps): ReactElement => {
  const [name, setName] = useState(user.name)
  const [email, setEmail] = useState(user.email)
  const [roleId, setRoleId] = useState(user.roleId ?? '')
  const [readOnly, setReadOnly] = useState(user.readOnly)
  const [scope, setScope] = useState<GroupScope>(user.role)
  const [pin, setPin] = useState('')
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const update = trpc.users.update.useMutation()
  const setUserPin = trpc.users.setPin.useMutation()
  const clearPin = trpc.users.clearPin.useMutation({
    onSuccess: onSaved,
    onError: (error) => {
      setConfirmingClear(false)
      setErrorMessage(trpcMessage(error))
    },
  })

  const nameChanged = name !== user.name && name !== ''
  const emailChanged = email !== user.email && email !== ''
  // Never for oneself, not even to the value it already has: the API refuses any
  // access field on the caller, and the fields are disabled below.
  const roleIdChanged = !isSelf && roleId !== '' && roleId !== user.roleId
  const readOnlyChanged = !isSelf && readOnly !== user.readOnly
  const scopeChanged = !isSelf && scope !== user.role
  const fieldsChanged = nameChanged || emailChanged || roleIdChanged || readOnlyChanged || scopeChanged
  const pinGiven = PIN.test(pin)
  const hasChanges = fieldsChanged || pinGiven
  const isPending = update.isPending || setUserPin.isPending

  const save = async (): Promise<void> => {
    try {
      if (fieldsChanged) {
        await update.mutateAsync({
          userId: user.id,
          ...(nameChanged ? { name } : {}),
          ...(emailChanged ? { email } : {}),
          ...(roleIdChanged ? { roleId } : {}),
          ...(readOnlyChanged ? { readOnly } : {}),
          ...(scopeChanged ? { role: scope } : {}),
        })
      }
      if (pinGiven) {
        await setUserPin.mutateAsync({ userId: user.id, pin })
      }
      onSaved()
    } catch (error) {
      setErrorMessage(trpcMessage(error))
    }
  }

  return (
    <>
      <Modal open={!confirmingClear} title={`Editar ${user.name}`} onClose={onClose} maxWidth="48rem">
        <form
          className={styles['users-page__form']}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            setErrorMessage(null)
            void save()
          }}
        >
          <InputText
            label="Nombre"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <InputText
            label="Email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <Select
            label="Rol"
            options={roleOptions(roles)}
            placeholder={user.roleId === null ? 'Sin rol' : undefined}
            value={roleId}
            disabled={isSelf}
            onChange={(event) => setRoleId(event.target.value)}
          />
          <ReadOnlyCheckbox checked={readOnly} disabled={isSelf} onChange={setReadOnly} />
          <Select
            label="Grupos que ve"
            options={SCOPE_OPTIONS}
            value={scope}
            disabled={isSelf}
            onChange={(event) => {
              const { value } = event.target
              if (isGroupScope(value)) {
                setScope(value)
              }
            }}
          />
          {isSelf ? <p className={styles['users-page__note']}>{SELF_ACCESS_NOTE}</p> : null}
          <PinField
            label={user.hasPin ? 'PIN nuevo (opcional)' : 'PIN (opcional)'}
            hint={user.hasPin
              ? 'Tiene PIN. Escribe cuatro dígitos para cambiarlo (también le desbloquea), o quítaselo.'
              : 'Sin PIN. Cuatro dígitos para entrar en la tablet de almacén, si su rol abre almacén.'}
            value={pin}
            onChange={setPin}
          />
          {user.hasPin ? (
            <div>
              <Button
                variant="danger"
                onClick={() => {
                  setErrorMessage(null)
                  setConfirmingClear(true)
                }}
                disabled={isPending || clearPin.isPending}
                label="Quitar PIN"
              />
            </div>
          ) : null}
          {errorMessage !== null && (
            <p role="alert" className={styles['users-page__error']}>{errorMessage}</p>
          )}
          <div className={styles['users-page__form-actions']}>
            <Button variant="secondary" onClick={onClose} disabled={isPending} label="Cancelar" />
            <Button
              type="submit"
              disabled={isPending || !hasChanges || (pin !== '' && !pinGiven)}
              label={isPending ? 'Guardando…' : 'Guardar cambios'}
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmingClear}
        variant="danger"
        title="Quitar PIN"
        message={`${user.name} ya no podrá entrar con PIN en la tablet de almacén; con su contraseña o con Google, sí.`}
        confirmLabel="Quitar PIN"
        isLoading={clearPin.isPending}
        onCancel={() => setConfirmingClear(false)}
        onConfirm={() => clearPin.mutate({ userId: user.id })}
      />
    </>
  )
}

type ResetPasswordFlowProps = {
  user: PlantUser
  onClose: () => void
  onDone: () => void
}

/**
 * A forgotten password: the manager sets a new one and tells the person.
 *
 * Two steps, and never two dialogs stacked at once — typing the password, then
 * confirming what it costs. `users.resetPassword` revokes every refresh token of
 * the target, so somebody working on a tablet right now is signed out by it, and
 * that is worth a deliberate second tap.
 */
const ResetPasswordFlow = ({ user, onClose, onDone }: ResetPasswordFlowProps): ReactElement => {
  const [newPassword, setNewPassword] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const resetPassword = trpc.users.resetPassword.useMutation({
    onSuccess: () => {
      setConfirming(false)
      setNewPassword('')
      setDone(true)
    },
    onError: (error) => {
      setConfirming(false)
      setErrorMessage(trpcMessage(error))
    },
  })

  return (
    <>
      <Modal open={!confirming} title={`Contraseña de ${user.name}`} onClose={onClose} maxWidth="48rem">
        <form
          className={styles['users-page__form']}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            setErrorMessage(null)
            setDone(false)
            setConfirming(true)
          }}
        >
          <InputText
            label="Contraseña nueva"
            type="password"
            autoComplete="new-password"
            hint={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres. Dísela a la persona.`}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
          {errorMessage !== null && (
            <p role="alert" className={styles['users-page__error']}>{errorMessage}</p>
          )}
          {done && (
            <p role="status" className={styles['users-page__ok']}>
              Contraseña cambiada. Sus sesiones se han cerrado.
            </p>
          )}
          <div className={styles['users-page__form-actions']}>
            <Button variant="secondary" onClick={done ? onDone : onClose} label={done ? 'Cerrar' : 'Cancelar'} />
            <Button
              type="submit"
              variant="danger"
              disabled={resetPassword.isPending || newPassword.length < MIN_PASSWORD_LENGTH}
              label="Restablecer contraseña"
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirming}
        variant="danger"
        title="Restablecer contraseña"
        message={`Se cerrarán todas las sesiones de ${user.name} y tendrá que entrar con la contraseña nueva.`}
        confirmLabel="Restablecer"
        isLoading={resetPassword.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => resetPassword.mutate({ userId: user.id, newPassword })}
      />
    </>
  )
}
